import React from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from "@mui/material";

const ScopeAccreditationCalibration = ({ data, labType }) => {
  if (!data || data.length === 0) return null;
  // GROUP DATA BY ASSESSOR + DISCIPLINE
  const groupedData = data.reduce((acc, item) => {
    const key = `${item.AssessorName}_${item.disciplineName}`;

    if (!acc[key]) {
      acc[key] = {
        assessor: item.AssessorName,
        discipline: item.disciplineName,
        group: item.groupName,
        scopes: []
      };
    }

    acc[key].scopes.push(item);
    return acc;
  }, {});

  // Maintain continuous serial number across all groups
  let serialNumber = 0;

  return (
   <Box sx={{ mb: 4 }}>
      <Card elevation={3}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            bgcolor: "#e3f2fd",
            p: 1,
            mb: 2,
            textAlign: "center"
          }}
        >
          Scope of Accreditation (Form 73)
        </Typography>

        {Object.values(groupedData).map((group, gIndex) => (
          <Box key={gIndex} sx={{ mb: 3, px: 2 }}>

            {/* ASSESSOR + DISCIPLINE HEADER */}
            <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
              Assessor: {group.assessor || "-"}
            </Typography>

            <Typography sx={{ fontWeight: "bold", mb: 1 }}>
              Discipline: {group.discipline || "-"}
            </Typography>

            <Typography sx={{ fontWeight: "bold", mb: 1 }}>
              Group: {group.group || "-"}
            </Typography>

            <TableContainer component={Paper}>
              <Table
                size="small"
                sx={{
                  border: "1px solid #bdbdbd",
                  "& th, & td": { border: "1px solid #bdbdbd" },
                  borderCollapse: "collapse"
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                    <TableCell align="center"><b>Sr No.</b></TableCell>
                    <TableCell align="center"><b>Scope Details</b></TableCell>
                    <TableCell align="center"><b>Range & Standards</b></TableCell>
                    <TableCell align="center"><b>Calibration and Measurement Capability</b></TableCell>
                    <TableCell align="center"><b>Assessment Details</b></TableCell>
                    <TableCell align="center"><b>Status</b></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {group.scopes.map((item, index) => {
                    serialNumber++; // Increment serial number for each scope
                    return (
                      <TableRow key={index}>
                        <TableCell align="center">{serialNumber}</TableCell>

                        <TableCell>
                          <div>
                            <strong>Test Code:</strong> {item.testCode || "-"}
                          </div>
                          <div>
                            <strong>Parameter/Device under Calibration:</strong>{" "}
                            {item.GroupTested || "-"}
                          </div>
                          <div>
                            <strong>Master Equipment used:</strong>{" "}
                            {item.EquipmentName || "-"}
                          </div>
                          <div>
                            <strong>Calibration Performed:</strong>{" "}
                            {item.TypeOfTest || "-"}
                          </div>
                          <div>
                            <strong>Remarks/Method used:</strong>{" "}
                            {item.scopeStandard || "-"}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div>
                            <strong>Type:</strong> {item.rangeType || "-"}
                          </div>
                          <div>
                            <strong>Facility:</strong>{" "}
                            {item.testPerformedAt || "-"}
                          </div>
                          <div>
                            <strong>Range(s) of Measurement:</strong>{" "}
                            {(item.RangeUL && item.RangeUL==="")
                              ? `${item.RangeLL || "-"} ${item.RangeLLUnit || ""} To ${item.RangeUL} ${item.RangeULUnit || ""}`
                              : `${item.RangeLL || "-"} ${item.RangeLLUnit || ""}`}
                          </div>
                          <div>
                            <strong>CMC:</strong>{" "}
                            {item.cv ? `CMC LL: ${item.cv}${item.mu}` : ""}{" "}
                            {item.cmcuppervalue
                              ? `CMC UL: ${item.cmcuppervalue}${item.cmcupperUnit}`
                              : ""}{" "}
                            {!item.cv && !item.cmcuppervalue ? "-" : ""}
                          </div>
                        </TableCell>

                        <TableCell>
                          <div>
                            <strong>Claimed by Laboratory:</strong>{" "}
                            {item.cv ? `CMC LL: ${item.cv}${item.mu}` : ""}{" "}
                            {item.cmcuppervalue
                              ? `CMC UL: ${item.cmcuppervalue}${item.cmcupperUnit}`
                              : ""}{" "}
                            {!item.cv && !item.cmcuppervalue ? "-" : ""}
                          </div>
                          <div style={{ marginBottom: 4 }}>
                            <strong>Observed by Assessor:</strong>{" "}
                            <strong
                              style={{
                                color:
                                  item.conclusion?.toLowerCase() === "competent"
                                    ? "#2e7d32"
                                    : item.conclusion
                                      ? "#c62828"
                                      : "inherit",
                                textTransform: "capitalize",
                              }}
                            >
                              {item.conclusion || "-"}
                            </strong>
                            {item.observation && (
                              <span
                                style={{
                                  color: "#555",
                                  fontStyle: "italic",
                                  marginLeft: 4,
                                }}
                              >
                                ({item.observation})
                              </span>
                            )}
                          </div>
                          <div>
                            <div style={{ marginTop: 2 }}>
                              <div>
                                <strong>Calibration Points Taken:</strong>{" "}
                                {item.calibrationTaken || "-"}
                              </div>
                              <div>
                                <strong>
                                  Recommended CMC by the Assessor:
                                </strong>{" "}
                                {item.CMC
                                  ? item.CMC
                                  : item.cv
                                    ? `CMC LL: ${item.cv}${item.mu}`
                                    : ""}{" "}
                                {item.cmcuppervalue
                                  ? `CMC UL: ${item.cmcuppervalue}${item.cmcupperUnit}`
                                  : ""}{" "}
                                {!item.cv && !item.cmcuppervalue ? "-" : ""}
                              </div>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div>
                            <strong>Assessment Technique:</strong>{" "}
                            {item.assessmentTechniqueUsed ||
                              item.scopeStandard ||
                              "-"}
                          </div>
                          {item.Remarks && (
                            <div>
                              <strong>Remarks:</strong> {item.Remarks}
                            </div>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            label={item.status || "N/A"}
                            size="small"
                            sx={{
                              backgroundColor:
                                item.status === "Adequate"
                                  ? "#4caf50"
                                  : item.status === "Inadequate"
                                    ? "#f44336"
                                    : item.status === "Not Applicable"
                                      ? "#ff9800"
                                      : "#9e9e9e",
                              color: "#fff",
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

          </Box>
        ))}
      </Card>
    </Box>
  );
};

export default ScopeAccreditationCalibration;
